// frontend/src/pages/Agreements/AgreementDetail.jsx
import { useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { getEntities } from "../../api/entity.api.js";
import {
  getAgreement,
  getAddendums,
  getAgreementFileUrl,
  moveAgreementToReview,
  reviewAgreement,
  terminateAgreement,
  updateAgreement,
  updatePaymentStatus,
  reviewAddendum,
  updateAddendum,
  uploadAgreementFile,
  uploadAddendumFile,
  getAddendumFileUrl,
  getAddendumPrevFileUrl,
} from "../../api/agreement.api.js";
import {
  uploadDraft,
  getDrafts,
  getDraftDownloadUrl,
  finalizeDraft,
  vendorAgree,
  activateInternal,
} from "../../api/draft.api.js";
import {
  Spinner,
  StatusBadge,
  ErrorMessage,
  formatDate,
} from "../../components/ui";
import {
  ChevronLeft,
  Download,
  Plus,
  CheckCircle,
  AlertCircle,
  Trash2,
  CreditCard,
  Pencil,
  X,
  Upload,
  Send,
  HeartHandshake,
  FileCheck,
} from "lucide-react";

// ─── Action label map for activity log ───────────────────────────────────────

const ACTION_LABELS = {
  SUBMITTED: "Submitted agreement",
  FILE_UPLOADED: "Uploaded agreement PDF",
  MOVED_TO_REVIEW: "Moved to Under Review",
  APPROVED: "Approved agreement",
  REVISION_REQUESTED: "Requested revision",
  TERMINATED: "Terminated agreement",
  PAYMENT_UPDATED: "Updated payment status",
  ADDENDUM_SUBMITTED: "Submitted addendum",
  ADDENDUM_FILE_UPLOADED: "Uploaded addendum PDF",
  ADDENDUM_APPROVED: "Approved addendum",
  ADDENDUM_REVISION_REQUESTED: "Requested addendum revision",
  ADDENDUM_REVISED: "Revised and resubmitted addendum",
  // Collaborative flow
  CREATED_FROM_PROPOSAL: "Agreement dibuat dari pengajuan",
  DRAFT_UPLOADED: "Draft diunggah",
  DRAFT_FINALIZED: "Legal finalisasi draft",
  VENDOR_AGREED: "Vendor menyetujui",
  AGREEMENT_ACTIVATED: "Perjanjian aktif",
};

// ─── Agreement review panel (master — DRAFT / UNDER_REVIEW) ───────────────────

function ReviewPanel({ agreement, onDone }) {
  const [action, setAction] = useState("approve");
  const [comment, setComment] = useState("");
  const [error, setError] = useState(null);

  const moveToReview = useMutation({
    mutationFn: () => moveAgreementToReview(agreement.id),
    onSuccess: onDone,
    onError: (err) => setError(err),
  });

  const review = useMutation({
    mutationFn: () => reviewAgreement(agreement.id, { action, comment }),
    onSuccess: () => {
      setError(null);
      onDone();
    },
    onError: (err) => setError(err),
  });

  if (agreement.status === "DRAFT") {
    return (
      <div className="card p-5">
        <h3 className="font-semibold text-gray-900 mb-3">Review Actions</h3>
        {error && (
          <div className="mb-3">
            <ErrorMessage error={error} />
          </div>
        )}
        <p className="text-sm text-gray-500 mb-3">
          Move to Under Review to begin evaluation.
        </p>
        <button
          onClick={() => moveToReview.mutate()}
          disabled={moveToReview.isPending}
          className="btn-primary w-full text-sm"
        >
          {moveToReview.isPending ? "Moving..." : "Move to Under Review"}
        </button>
      </div>
    );
  }

  if (agreement.status !== "UNDER_REVIEW") return null;

  return (
    <div className="card p-5">
      <h3 className="font-semibold text-gray-900 mb-4">Review Decision</h3>
      {error && (
        <div className="mb-3">
          <ErrorMessage error={error} />
        </div>
      )}
      <div className="space-y-3">
        <div className="flex gap-2">
          {[
            { val: "approve", label: "Approve", icon: CheckCircle },
            {
              val: "request_revision",
              label: "Request Revision",
              icon: AlertCircle,
            },
          ].map(({ val, label, icon: Icon }) => (
            <button
              key={val}
              onClick={() => setAction(val)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                action === val
                  ? "border-brand-500 bg-brand-50 text-brand-700"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>
        <div>
          <label className="label text-xs">
            Comment{" "}
            {action === "request_revision" ? "(required)" : "(optional)"}
          </label>
          <textarea
            className="input text-sm"
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={
              action === "approve"
                ? "Optional note..."
                : "Describe what needs to be revised..."
            }
          />
        </div>
        <button
          onClick={() => review.mutate()}
          disabled={
            review.isPending ||
            (action === "request_revision" && !comment.trim())
          }
          className={`w-full text-sm font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 ${
            action === "approve"
              ? "bg-green-600 hover:bg-green-700 text-white"
              : "bg-amber-500 hover:bg-amber-600 text-white"
          }`}
        >
          {review.isPending
            ? "Submitting..."
            : action === "approve"
              ? "Approve Agreement"
              : "Request Revision"}
        </button>
      </div>
    </div>
  );
}

// ─── Terminate panel (master — APPROVED only) ─────────────────────────────────

function TerminatePanel({ agreement, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);

  const terminate = useMutation({
    mutationFn: () => terminateAgreement(agreement.id, reason),
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
    onError: (err) => setError(err),
  });

  if (agreement.status !== "APPROVED") return null;

  return (
    <div className="card p-5">
      <h3 className="font-semibold text-gray-900 mb-3">Terminate Agreement</h3>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="btn-danger w-full text-sm flex items-center justify-center gap-2"
        >
          <Trash2 size={14} /> Terminate
        </button>
      ) : (
        <div className="space-y-3">
          {error && <ErrorMessage error={error} />}
          <div>
            <label className="label text-xs">Reason (optional)</label>
            <textarea
              className="input text-sm"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => terminate.mutate()}
              disabled={terminate.isPending}
              className="btn-danger flex-1 text-sm"
            >
              {terminate.isPending ? "Terminating..." : "Confirm"}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="btn-secondary flex-1 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Payment status panel (master) ────────────────────────────────────────────

function PaymentPanel({ agreement, onDone }) {
  const [open, setOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(agreement.paymentStatus);
  const [paymentDate, setPaymentDate] = useState(
    agreement.paymentDate ? agreement.paymentDate.split("T")[0] : "",
  );
  const [error, setError] = useState(null);

  const update = useMutation({
    mutationFn: () =>
      updatePaymentStatus(agreement.id, {
        paymentStatus,
        paymentDate: paymentDate || undefined,
      }),
    onSuccess: () => {
      setOpen(false);
      setError(null);
      onDone();
    },
    onError: (err) => setError(err),
  });

  if (!["APPROVED", "EXPIRED", "TERMINATED"].includes(agreement.status))
    return null;

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">Payment Status</h3>
        {!open && (
          <button
            onClick={() => setOpen(true)}
            className="text-xs text-brand-500 hover:underline flex items-center gap-1"
          >
            <Pencil size={12} /> Update
          </button>
        )}
      </div>

      {!open ? (
        <div className="flex items-center gap-2">
          <CreditCard size={15} className="text-gray-400" />
          <span
            className={`badge ${
              agreement.paymentStatus === "PAID"
                ? "bg-green-100 text-green-700"
                : agreement.paymentStatus === "PARTIAL"
                  ? "bg-amber-100 text-amber-700"
                  : "bg-red-100 text-red-700"
            }`}
          >
            {agreement.paymentStatus}
          </span>
          {agreement.paymentDate && (
            <span className="text-xs text-gray-500">
              {formatDate(agreement.paymentDate)}
            </span>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {error && <ErrorMessage error={error} />}
          <div>
            <label className="label text-xs">Status</label>
            <select
              className="input text-sm"
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
            >
              <option value="UNPAID">UNPAID</option>
              <option value="PARTIAL">PARTIAL</option>
              <option value="PAID">PAID</option>
            </select>
          </div>
          <div>
            <label className="label text-xs">Payment Date (optional)</label>
            <input
              type="date"
              className="input text-sm"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => update.mutate()}
              disabled={update.isPending}
              className="btn-primary flex-1 text-sm"
            >
              {update.isPending ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="btn-secondary text-sm px-3"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Entity assign panel (master — inline on entity card) ────────────────────

function EntityAssignPanel({ agreement, onDone }) {
  const [open, setOpen] = useState(false);
  const [entityCacheId, setEntityCacheId] = useState(
    agreement.entityCache?.id ?? "",
  );
  const [error, setError] = useState(null);

  const { data: entities } = useQuery({
    queryKey: ["entities"],
    queryFn: () => getEntities(),
    enabled: open,
  });

  const save = useMutation({
    mutationFn: () => updateAgreement(agreement.id, { entityCacheId }),
    onSuccess: () => {
      setOpen(false);
      setError(null);
      onDone();
    },
    onError: (err) => setError(err),
  });

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-2 flex items-center gap-1 text-xs text-brand-500 hover:underline"
      >
        <Pencil size={11} />
        {agreement.entityCache ? "Change entity" : "Assign entity"}
      </button>
    );
  }

  return (
    <div className="mt-3 space-y-2">
      {error && <ErrorMessage error={error} />}
      <select
        className="input text-sm"
        value={entityCacheId}
        onChange={(e) => setEntityCacheId(e.target.value)}
      >
        <option value="">Select PT...</option>
        {entities?.map((e) => (
          <option key={e.id} value={e.id}>
            {e.name}
          </option>
        ))}
      </select>
      <div className="flex gap-2">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || !entityCacheId}
          className="btn-primary flex-1 text-xs py-1.5"
        >
          {save.isPending ? "Saving..." : "Save"}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="btn-secondary text-xs px-3"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}

// ─── Draft edit panel (vendor/partner — DRAFT or REVISION_REQUESTED) ──────────

function DraftEditPanel({ agreement, onDone }) {
  const { isMaster } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: agreement.title ?? "",
    scope: agreement.scope ?? "",
    stakeholders: agreement.stakeholders ?? "",
    objectOfAgreement: agreement.objectOfAgreement ?? "",
    entityCacheId: agreement.entityCache?.id ?? "",
    startDate: agreement.startDate ? agreement.startDate.split("T")[0] : "",
    endDate: agreement.endDate ? agreement.endDate.split("T")[0] : "",
    paymentDeadline: agreement.paymentDeadline
      ? agreement.paymentDeadline.split("T")[0]
      : "",
  });
  const [error, setError] = useState(null);

  // Only fetch entities when master admin opens the panel
  const { data: entities } = useQuery({
    queryKey: ["entities"],
    queryFn: () => getEntities(),
    enabled: isMaster && open,
  });

  const save = useMutation({
    mutationFn: () =>
      updateAgreement(agreement.id, {
        ...form,
        paymentDeadline: form.paymentDeadline || undefined,
        stakeholders: form.stakeholders || undefined,
        objectOfAgreement: form.objectOfAgreement || undefined,
        // Only send entityCacheId if master changed it
        entityCacheId:
          isMaster && form.entityCacheId !== agreement.entityCache?.id
            ? form.entityCacheId
            : undefined,
      }),
    onSuccess: () => {
      setOpen(false);
      setError(null);
      onDone();
    },
    onError: (err) => setError(err),
  });

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  if (!["DRAFT", "REVISION_REQUESTED"].includes(agreement.status)) return null;

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">Edit Agreement</h3>
        {open && (
          <button
            onClick={() => setOpen(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {agreement.status === "REVISION_REQUESTED" && !open && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs font-semibold text-amber-700 mb-1">
            Revision Requested
          </p>
          <p className="text-xs text-amber-700">{agreement.reviewComment}</p>
        </div>
      )}

      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="btn-secondary w-full text-sm flex items-center justify-center gap-2"
        >
          <Pencil size={14} /> Edit & Resubmit
        </button>
      ) : (
        <div className="space-y-3">
          {error && <ErrorMessage error={error} />}
          <div>
            <label className="label text-xs">Title</label>
            <input
              className="input text-sm"
              value={form.title}
              onChange={set("title")}
            />
          </div>

          {/* Entity — master admin only */}
          {isMaster && (
            <div>
              <label className="label text-xs">Rhaya Entity (PT)</label>
              <select
                className="input text-sm"
                value={form.entityCacheId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, entityCacheId: e.target.value }))
                }
              >
                <option value="">Select PT...</option>
                {entities?.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="label text-xs">Scope</label>
            <textarea
              className="input text-sm"
              rows={3}
              value={form.scope}
              onChange={set("scope")}
            />
          </div>
          <div>
            <label className="label text-xs">Object of Agreement</label>
            <textarea
              className="input text-sm"
              rows={2}
              value={form.objectOfAgreement}
              onChange={set("objectOfAgreement")}
            />
          </div>
          <div>
            <label className="label text-xs">Stakeholders</label>
            <input
              className="input text-sm"
              value={form.stakeholders}
              onChange={set("stakeholders")}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label text-xs">Start Date</label>
              <input
                type="date"
                className="input text-sm"
                value={form.startDate}
                onChange={set("startDate")}
              />
            </div>
            <div>
              <label className="label text-xs">End Date</label>
              <input
                type="date"
                className="input text-sm"
                value={form.endDate}
                onChange={set("endDate")}
                min={form.startDate}
              />
            </div>
          </div>
          <div>
            <label className="label text-xs">Payment Deadline</label>
            <input
              type="date"
              className="input text-sm"
              value={form.paymentDeadline}
              onChange={set("paymentDeadline")}
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="btn-primary flex-1 text-sm"
            >
              {save.isPending ? "Saving..." : "Save & Resubmit"}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="btn-secondary text-sm px-3"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Addendum review sub-panel (inline, master only) ─────────────────────────

function AddendumReviewInline({ addendum, agreementId, onDone }) {
  const [action, setAction] = useState("approve");
  const [comment, setComment] = useState("");
  const [error, setError] = useState(null);

  const review = useMutation({
    mutationFn: () =>
      reviewAddendum(agreementId, addendum.id, { action, comment }),
    onSuccess: () => {
      setError(null);
      onDone();
    },
    onError: (err) => setError(err),
  });

  if (!["DRAFT", "UNDER_REVIEW", "REVISED"].includes(addendum.status))
    return null;

  return (
    <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
      <p className="text-xs font-semibold text-gray-600">
        Review this addendum
      </p>
      <div className="flex gap-1.5">
        {[
          {
            val: "approve",
            label: "Approve",
            activeClass: "bg-green-600 text-white",
          },
          {
            val: "request_revision",
            label: "Revision",
            activeClass: "bg-amber-500 text-white",
          },
        ].map(({ val, label, activeClass }) => (
          <button
            key={val}
            onClick={() => setAction(val)}
            className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition-colors ${
              action === val
                ? activeClass
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <textarea
        className="input text-xs"
        rows={2}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        placeholder={
          action === "approve"
            ? "Optional note..."
            : "What needs to be revised? (required)"
        }
      />
      {error && <ErrorMessage error={error} />}
      <button
        onClick={() => review.mutate()}
        disabled={
          review.isPending || (action === "request_revision" && !comment.trim())
        }
        className="btn-primary w-full text-xs py-1.5 disabled:opacity-50"
      >
        {review.isPending
          ? "Submitting..."
          : `Confirm — ${action === "approve" ? "Approve" : "Request Revision"}`}
      </button>
    </div>
  );
}

// ─── Addendum edit inline (shown to submitter when REVISION_REQUESTED) ───────

function AddendumEditInline({ addendum, agreementId, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    title: addendum.title ?? "",
    description: addendum.description ?? "",
    scope: addendum.scope ?? "",
    objectOfAgreement: addendum.objectOfAgreement ?? "",
    stakeholders: addendum.stakeholders ?? "",
    startDate: addendum.startDate ? addendum.startDate.split("T")[0] : "",
    endDate: addendum.endDate ? addendum.endDate.split("T")[0] : "",
    paymentDeadline: addendum.paymentDeadline
      ? addendum.paymentDeadline.split("T")[0]
      : "",
  });

  const save = useMutation({
    mutationFn: async () => {
      await updateAddendum(agreementId, addendum.id, {
        ...form,
        scope: form.scope || undefined,
        objectOfAgreement: form.objectOfAgreement || undefined,
        stakeholders: form.stakeholders || undefined,
        startDate: form.startDate || undefined,
        endDate: form.endDate || undefined,
        paymentDeadline: form.paymentDeadline || undefined,
      });
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await uploadAddendumFile(agreementId, addendum.id, fd);
      }
    },
    onSuccess: () => {
      setOpen(false);
      setError(null);
      onDone();
    },
    onError: (err) => setError(err),
  });

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  if (addendum.status !== "REVISION_REQUESTED") return null;

  return (
    <div className="mt-3 pt-3 border-t border-gray-200">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 text-xs text-brand-600 hover:underline"
        >
          <Pencil size={11} /> Edit & Resubmit
        </button>
      ) : (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-600">Edit Addendum</p>
          {error && <ErrorMessage error={error} />}

          <div>
            <label className="label text-xs">Title</label>
            <input
              className="input text-xs"
              value={form.title}
              onChange={set("title")}
            />
          </div>
          <div>
            <label className="label text-xs">Description of Changes</label>
            <textarea
              className="input text-xs"
              rows={3}
              value={form.description}
              onChange={set("description")}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label text-xs">New Start Date</label>
              <input
                type="date"
                className="input text-xs"
                value={form.startDate}
                onChange={set("startDate")}
              />
            </div>
            <div>
              <label className="label text-xs">New End Date</label>
              <input
                type="date"
                className="input text-xs"
                value={form.endDate}
                onChange={set("endDate")}
              />
            </div>
          </div>
          <div>
            <label className="label text-xs">New Scope</label>
            <textarea
              className="input text-xs"
              rows={2}
              value={form.scope}
              onChange={set("scope")}
            />
          </div>
          <div>
            <label className="label text-xs">New Object of Agreement</label>
            <textarea
              className="input text-xs"
              rows={2}
              value={form.objectOfAgreement}
              onChange={set("objectOfAgreement")}
            />
          </div>
          <div>
            <label className="label text-xs">New Stakeholders</label>
            <input
              className="input text-xs"
              value={form.stakeholders}
              onChange={set("stakeholders")}
            />
          </div>
          <div>
            <label className="label text-xs">New Payment Deadline</label>
            <input
              type="date"
              className="input text-xs"
              value={form.paymentDeadline}
              onChange={set("paymentDeadline")}
            />
          </div>

          {/* File upload */}
          <div>
            <label className="label text-xs">
              Addendum PDF{" "}
              {addendum.fileName && (
                <span className="text-gray-400 font-normal">
                  (current: {addendum.fileName})
                </span>
              )}
            </label>
            <label
              className={`flex items-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                file
                  ? "border-brand-500 bg-brand-50"
                  : "border-gray-200 hover:border-brand-300"
              }`}
            >
              <Upload
                size={14}
                className={file ? "text-brand-500" : "text-gray-400"}
              />
              <span
                className={`text-xs ${file ? "text-brand-700 font-medium" : "text-gray-500"}`}
              >
                {file ? file.name : "Click to replace PDF (optional)"}
              </span>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
            {file && (
              <button
                type="button"
                onClick={() => setFile(null)}
                className="mt-1 text-xs text-red-500 hover:underline"
              >
                Remove
              </button>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="btn-primary flex-1 text-xs py-1.5"
            >
              {save.isPending ? "Saving..." : "Save & Resubmit"}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="btn-secondary text-xs px-3"
            >
              <X size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Addendum timeline ────────────────────────────────────────────────────────

function AddendumTimeline({
  agreementId,
  canSubmit,
  agreementStatus,
  isMaster,
  isOwner,
  user,
  onReviewed,
  allLogs,
  agreementEntityCache,
}) {
  const { data: addendums, isLoading } = useQuery({
    queryKey: ["addendums", agreementId],
    queryFn: () => getAddendums(agreementId),
  });

  const showSubmit = canSubmit && agreementStatus === "APPROVED";

  if (isLoading) return <Spinner />;

  async function downloadAddendumFile(addendumId) {
    try {
      const { url } = await getAddendumFileUrl(agreementId, addendumId);
      window.open(url, "_blank");
    } catch (err) {
      alert(err.message);
    }
  }

  async function downloadPrevFile(addendumId) {
    try {
      const { url } = await getAddendumPrevFileUrl(agreementId, addendumId);
      window.open(url, "_blank");
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <h2 className="font-semibold text-gray-900">Addendum History</h2>
        {showSubmit && (
          <Link
            to={`/agreements/${agreementId}/addendum/new`}
            className="btn-secondary flex items-center gap-1.5 text-xs py-1.5 px-3"
          >
            <Plus size={13} /> Add Addendum
          </Link>
        )}
      </div>

      {!addendums?.length ? (
        <p className="px-6 py-6 text-sm text-gray-400 text-center">
          No addendums yet
        </p>
      ) : (
        <div className="px-6 py-4">
          <div className="relative">
            <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200" />
            <div className="space-y-4">
              {addendums.map((a, index) => (
                <div key={a.id} className="relative pl-8">
                  <div
                    className={`absolute left-1.5 top-1.5 w-3 h-3 rounded-full border-2 border-white ${
                      a.status === "APPROVED"
                        ? "bg-green-500"
                        : a.status === "REVISION_REQUESTED"
                          ? "bg-amber-500"
                          : a.status === "REVISED"
                            ? "bg-blue-400"
                            : a.status === "UNDER_REVIEW"
                              ? "bg-blue-500"
                              : "bg-gray-300"
                    }`}
                  />
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {a.title}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Addendum {addendums.length - index} ·{" "}
                          {formatDate(a.createdAt)}
                        </p>
                      </div>
                      <StatusBadge status={a.status} />
                    </div>

                    {a.description && (
                      <p className="text-xs text-gray-600 mt-2 italic">
                        {a.description}
                      </p>
                    )}

                    {/* Proposed changes — shown while addendum is pending review */}
                    {[
                      "DRAFT",
                      "REVISED",
                      "UNDER_REVIEW",
                      "REVISION_REQUESTED",
                    ].includes(a.status) &&
                      (() => {
                        const proposed = [
                          [
                            "New Entity",
                            a.entityCacheId
                              ? agreementEntityCache?.id === a.entityCacheId
                                ? null
                                : a.entityCacheId
                              : null,
                          ],
                          [
                            "New Start Date",
                            a.startDate ? formatDate(a.startDate) : null,
                          ],
                          [
                            "New End Date",
                            a.endDate ? formatDate(a.endDate) : null,
                          ],
                          [
                            "New Payment Deadline",
                            a.paymentDeadline
                              ? formatDate(a.paymentDeadline)
                              : null,
                          ],
                          ["New Scope", a.scope],
                          ["New Object", a.objectOfAgreement],
                          ["New Stakeholders", a.stakeholders],
                        ].filter(([, v]) => v);

                        if (!proposed.length) return null;

                        return (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs font-semibold text-gray-500 mb-1.5">
                              Proposed changes
                            </p>
                            <dl className="space-y-1">
                              {proposed.map(([k, v]) => (
                                <div key={k} className="flex gap-2 text-xs">
                                  <dt className="text-gray-400 flex-shrink-0 w-32">
                                    {k}
                                  </dt>
                                  <dd className="text-gray-800 font-medium">
                                    {v}
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                        );
                      })()}

                    {/* Who / when meta — shows full revision history */}
                    <div className="mt-2 space-y-1">
                      {(() => {
                        const addendumLogs = (allLogs ?? []).filter(
                          (l) => l.addendumId === a.id,
                        );
                        const submittedLog = addendumLogs.find(
                          (l) => l.action === "ADDENDUM_SUBMITTED",
                        );
                        const revisedLogs = addendumLogs.filter(
                          (l) => l.action === "ADDENDUM_REVISED",
                        );
                        const revisionRequestedLogs = addendumLogs.filter(
                          (l) => l.action === "ADDENDUM_REVISION_REQUESTED",
                        );
                        const reviewedLog = addendumLogs.find(
                          (l) => l.action === "ADDENDUM_APPROVED",
                        );
                        return (
                          <>
                            {submittedLog && (
                              <p className="text-xs text-gray-500">
                                <span className="font-medium">
                                  Submitted by
                                </span>{" "}
                                {submittedLog.actorName}
                                <span className="text-gray-400 ml-1 capitalize">
                                  ({submittedLog.actorType})
                                </span>
                                <span className="text-gray-400">
                                  {" "}
                                  · {formatDate(submittedLog.createdAt)}
                                </span>
                              </p>
                            )}
                            {revisionRequestedLogs.map((log, i) => (
                              <p
                                key={`rev-req-${i}`}
                                className="text-xs text-amber-600"
                              >
                                <span className="font-medium">
                                  Revision {i + 1} requested by
                                </span>{" "}
                                {log.actorName}
                                <span className="text-gray-400">
                                  {" "}
                                  · {formatDate(log.createdAt)}
                                </span>
                                {log.comment && (
                                  <span className="italic text-gray-500">
                                    {" "}
                                    — "{log.comment}"
                                  </span>
                                )}
                              </p>
                            ))}
                            {revisedLogs.map((log, i) => (
                              <p
                                key={`revised-${i}`}
                                className="text-xs text-blue-600"
                              >
                                <span className="font-medium">
                                  Revised {i + 1}× by
                                </span>{" "}
                                {log.actorName}
                                <span className="text-gray-400 ml-1 capitalize">
                                  ({log.actorType})
                                </span>
                                <span className="text-gray-400">
                                  {" "}
                                  · {formatDate(log.createdAt)}
                                </span>
                              </p>
                            ))}
                            {reviewedLog && (
                              <p className="text-xs text-green-600">
                                <span className="font-medium">Approved by</span>{" "}
                                {reviewedLog.actorName}
                                <span className="text-gray-400">
                                  {" "}
                                  · {formatDate(reviewedLog.createdAt)}
                                </span>
                              </p>
                            )}
                          </>
                        );
                      })()}
                    </div>

                    {/* Addendum PDF — visible to all while pending or after approval */}
                    {a.fileUrl && (
                      <button
                        onClick={() => downloadAddendumFile(a.id)}
                        className="mt-2 flex items-center gap-1.5 text-xs text-brand-600 hover:underline"
                      >
                        <Download size={12} /> Download addendum PDF
                      </button>
                    )}

                    {/* Previous version snapshot — shown for approved addendums */}
                    {a.status === "APPROVED" && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="text-xs font-semibold text-gray-500">
                            Previous version
                          </p>
                          <span className="badge bg-red-100 text-red-600 text-[10px]">
                            Void — superseded by this addendum
                          </span>
                        </div>
                        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                          {[
                            [
                              "Entity",
                              a.prevEntityCacheId
                                ? a.prevEntityCacheId ===
                                  agreementEntityCache?.id
                                  ? agreementEntityCache?.name
                                  : `ID: ${a.prevEntityCacheId}`
                                : null,
                            ],
                            ["Start Date", formatDate(a.prevStartDate)],
                            ["End Date", formatDate(a.prevEndDate)],
                            [
                              "Payment Deadline",
                              formatDate(a.prevPaymentDeadline),
                            ],
                            ["Scope", a.prevScope],
                            ["Object", a.prevObjectOfAgreement],
                            ["Stakeholders", a.prevStakeholders],
                          ]
                            .filter(([, v]) => v && v !== "—")
                            .map(([k, v]) => (
                              <div key={k}>
                                <dt className="text-gray-400 uppercase tracking-wide text-[10px]">
                                  {k}
                                </dt>
                                <dd className="text-gray-700">{v}</dd>
                              </div>
                            ))}
                        </dl>
                        {/* Previous agreement PDF download */}
                        {a.prevFileUrl && (
                          <button
                            onClick={() => downloadPrevFile(a.id)}
                            className="mt-2 flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            <Download size={12} /> Download previous agreement
                            PDF ({a.prevFileName})
                          </button>
                        )}
                      </div>
                    )}

                    {a.reviewComment && (
                      <p className="text-xs text-amber-700 mt-2 italic">
                        Review comment: "{a.reviewComment}"
                      </p>
                    )}

                    {/* Edit panel — shown to whoever submitted when REVISION_REQUESTED */}
                    {(() => {
                      const submittedByAdmin = a.submitterType === "admin";
                      const isSubmitter =
                        (submittedByAdmin && isMaster) ||
                        (!submittedByAdmin && isOwner);
                      return (
                        isSubmitter && (
                          <AddendumEditInline
                            addendum={a}
                            agreementId={agreementId}
                            onDone={onReviewed}
                          />
                        )
                      );
                    })()}

                    {/* Review panel — shown to whoever DIDN'T submit this addendum */}
                    {(() => {
                      const submittedByAdmin = a.submitterType === "admin";
                      const canReview =
                        // Admin submitted → counterparty (this user) reviews
                        (submittedByAdmin && isOwner) ||
                        // Counterparty submitted → master admin reviews
                        (!submittedByAdmin && isMaster);
                      return (
                        canReview && (
                          <AddendumReviewInline
                            addendum={a}
                            agreementId={agreementId}
                            onDone={onReviewed}
                          />
                        )
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Draft exchange section (collaborative flow) ──────────────────────────────

const DRAFT_TURN_LABELS = {
  DRAFTING_VENDOR_TURN: { text: "Giliran Vendor mengunggah / merespons draft", color: "text-sky-700 bg-sky-50 border-sky-200" },
  DRAFTING_LEGAL_TURN: { text: "Giliran Legal mengunggah / merespons draft", color: "text-indigo-700 bg-indigo-50 border-indigo-200" },
  AWAITING_VENDOR_AGREEMENT: { text: "Menunggu persetujuan vendor", color: "text-amber-700 bg-amber-50 border-amber-200" },
  ACTIVE: { text: "Perjanjian aktif — kedua pihak telah menyetujui", color: "text-green-700 bg-green-50 border-green-200" },
};

function DraftExchangeSection({ agreement, isMaster, isVendor, isOwner, onDone }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [error, setError] = useState(null);

  const isCollaborative = agreement.agreementType === "COLLABORATIVE";
  const isInternal = agreement.agreementType === "INTERNAL";

  const draftStatuses = [
    "DRAFTING_VENDOR_TURN",
    "DRAFTING_LEGAL_TURN",
    "AWAITING_VENDOR_AGREEMENT",
  ];
  const inDraftLoop = draftStatuses.includes(agreement.status);

  const { data: drafts, isLoading: draftsLoading } = useQuery({
    queryKey: ["drafts", agreement.id],
    queryFn: () => getDrafts(agreement.id),
    enabled: isCollaborative,
  });

  const upload = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("file", file);
      if (notes.trim()) fd.append("notes", notes.trim());
      return uploadDraft(agreement.id, fd);
    },
    onSuccess: () => {
      setFile(null);
      setNotes("");
      setUploadOpen(false);
      setError(null);
      qc.invalidateQueries({ queryKey: ["agreement", agreement.id] });
      qc.invalidateQueries({ queryKey: ["drafts", agreement.id] });
      onDone?.();
    },
    onError: (e) => setError(e.response?.data?.error || "Gagal mengunggah draft"),
  });

  const finalize = useMutation({
    mutationFn: () => finalizeDraft(agreement.id),
    onSuccess: () => {
      setError(null);
      onDone?.();
    },
    onError: (e) => setError(e.response?.data?.error || "Gagal finalisasi draft"),
  });

  const agree = useMutation({
    mutationFn: () => vendorAgree(agreement.id),
    onSuccess: () => {
      setError(null);
      onDone?.();
    },
    onError: (e) => setError(e.response?.data?.error || "Gagal menyetujui perjanjian"),
  });

  const activate = useMutation({
    mutationFn: () => activateInternal(agreement.id),
    onSuccess: () => {
      setError(null);
      onDone?.();
    },
    onError: (e) => setError(e.response?.data?.error || "Gagal mengaktifkan perjanjian"),
  });

  const downloadDraft = async (draftId, fileName) => {
    try {
      const { url } = await getDraftDownloadUrl(agreement.id, draftId);
      window.open(url, "_blank");
    } catch (err) {
      alert(err.message);
    }
  };

  // Can this user upload a draft right now?
  const canUploadDraft = isCollaborative && inDraftLoop && (
    (isMaster && agreement.status !== "AWAITING_VENDOR_AGREEMENT") ||
    (isOwner && (agreement.status === "DRAFTING_VENDOR_TURN" || agreement.status === "AWAITING_VENDOR_AGREEMENT"))
  );

  // Legal can finalize when it's vendor's turn to respond (meaning legal just uploaded)
  const canFinalize = isMaster &&
    isCollaborative &&
    agreement.status === "DRAFTING_VENDOR_TURN";

  const canVendorAgree = isOwner &&
    isCollaborative &&
    agreement.status === "AWAITING_VENDOR_AGREEMENT";

  const canActivateInternal = isMaster &&
    isInternal &&
    agreement.status !== "ACTIVE" &&
    agreement.status !== "TERMINATED";

  if (!isCollaborative && !isInternal) return null;

  const turnInfo = DRAFT_TURN_LABELS[agreement.status];

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="font-semibold text-gray-900">
          {isInternal ? "Aktivasi Perjanjian" : "Pertukaran Draft"}
        </h2>
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Status banner */}
        {turnInfo && (
          <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${turnInfo.color}`}>
            {turnInfo.text}
          </div>
        )}

        {/* Internal: simple activate button */}
        {canActivateInternal && (
          <div className="space-y-2">
            {error && <ErrorMessage error={error} />}
            <button
              onClick={() => activate.mutate()}
              disabled={activate.isPending}
              className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
            >
              <FileCheck size={15} />
              {activate.isPending ? "Mengaktifkan..." : "Aktifkan Perjanjian"}
            </button>
          </div>
        )}

        {/* Upload draft form */}
        {canUploadDraft && (
          <div>
            {!uploadOpen ? (
              <button
                onClick={() => setUploadOpen(true)}
                className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
              >
                <Upload size={14} /> Unggah Draft DOCX
              </button>
            ) : (
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-700">Unggah Draft Baru</p>

                <input
                  ref={fileRef}
                  type="file"
                  accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files[0] || null)}
                />
                {file ? (
                  <div className="flex items-center gap-2 p-2.5 border border-brand-200 bg-brand-50 rounded-lg">
                    <Upload size={13} className="text-brand-500 shrink-0" />
                    <span className="text-sm text-brand-700 font-medium flex-1 truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <X size={13} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2.5 w-full border-2 border-dashed border-gray-200 rounded-lg text-sm text-gray-500 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600 transition-colors"
                  >
                    <Upload size={14} /> Pilih file DOCX
                  </button>
                )}

                <div>
                  <label className="label text-xs">Catatan (opsional)</label>
                  <textarea
                    className="input text-sm"
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Catatan untuk pihak lain..."
                  />
                </div>

                {error && <ErrorMessage error={error} />}

                <div className="flex gap-2">
                  <button
                    onClick={() => upload.mutate()}
                    disabled={!file || upload.isPending}
                    className="btn-primary flex-1 flex items-center justify-center gap-1.5 text-sm py-1.5 disabled:opacity-50"
                  >
                    <Send size={13} />
                    {upload.isPending ? "Mengunggah..." : "Kirim Draft"}
                  </button>
                  <button
                    onClick={() => { setUploadOpen(false); setFile(null); setNotes(""); setError(null); }}
                    className="btn-secondary text-sm px-3"
                  >
                    <X size={13} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Legal: finalize draft */}
        {canFinalize && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">
              Jika draft sudah final, klik Finalisasi untuk meminta persetujuan vendor.
            </p>
            {error && <ErrorMessage error={error} />}
            <button
              onClick={() => finalize.mutate()}
              disabled={finalize.isPending}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 text-sm transition-colors"
            >
              <FileCheck size={15} />
              {finalize.isPending ? "Memfinalisasi..." : "Finalisasi Draft — Minta Persetujuan Vendor"}
            </button>
          </div>
        )}

        {/* Vendor: saya setuju */}
        {canVendorAgree && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">
              Tim Legal telah memfinalisasi draft. Klik tombol di bawah untuk menyetujui perjanjian ini.
            </p>
            {error && <ErrorMessage error={error} />}
            <button
              onClick={() => agree.mutate()}
              disabled={agree.isPending}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 text-sm transition-colors"
            >
              <HeartHandshake size={15} />
              {agree.isPending ? "Memproses..." : "Saya Setuju — Aktifkan Perjanjian"}
            </button>
          </div>
        )}

        {/* Draft history */}
        {isCollaborative && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Riwayat Draft
            </p>
            {draftsLoading ? (
              <Spinner />
            ) : !drafts?.length ? (
              <p className="text-sm text-gray-400 italic text-center py-4">
                Belum ada draft yang diunggah.
              </p>
            ) : (
              <div className="relative">
                <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200" />
                <div className="space-y-3">
                  {drafts.map((d, i) => {
                    const isLatest = i === drafts.length - 1;
                    const byLegal = d.submitterType === "admin";
                    return (
                      <div key={d.id} className="relative pl-8">
                        <div
                          className={`absolute left-1.5 top-2 w-3 h-3 rounded-full border-2 border-white ${
                            isLatest ? "bg-brand-500" : "bg-gray-300"
                          }`}
                        />
                        <div className={`rounded-lg p-3 ${isLatest ? "bg-brand-50 border border-brand-100" : "bg-gray-50"}`}>
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Draft #{d.draftNumber}
                                {isLatest && (
                                  <span className="ml-1.5 badge bg-brand-100 text-brand-700 text-[10px]">
                                    Terbaru
                                  </span>
                                )}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Oleh{" "}
                                <span className="font-medium">
                                  {byLegal ? "Tim Legal" : "Vendor"}
                                </span>{" "}
                                · {new Date(d.submittedAt).toLocaleDateString("id-ID", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                })}
                              </p>
                              {d.notes && (
                                <p className="text-xs text-gray-600 mt-1 italic">"{d.notes}"</p>
                              )}
                            </div>
                            <button
                              onClick={() => downloadDraft(d.id, d.fileName)}
                              className="flex items-center gap-1 text-xs text-brand-600 hover:underline shrink-0"
                            >
                              <Download size={12} /> Unduh
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Agreement timestamps */}
        {(agreement.legalAgreedAt || agreement.vendorAgreedAt || agreement.activatedAt) && (
          <div className="pt-3 border-t border-gray-100 space-y-1.5">
            {agreement.legalAgreedAt && (
              <p className="text-xs text-gray-500">
                <CheckCircle size={12} className="inline mr-1 text-green-500" />
                Legal setuju · {new Date(agreement.legalAgreedAt).toLocaleDateString("id-ID")}
              </p>
            )}
            {agreement.vendorAgreedAt && (
              <p className="text-xs text-gray-500">
                <CheckCircle size={12} className="inline mr-1 text-green-500" />
                Vendor setuju · {new Date(agreement.vendorAgreedAt).toLocaleDateString("id-ID")}
              </p>
            )}
            {agreement.activatedAt && (
              <p className="text-xs text-gray-500">
                <CheckCircle size={12} className="inline mr-1 text-green-500" />
                Aktif sejak · {new Date(agreement.activatedAt).toLocaleDateString("id-ID")}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AgreementDetail() {
  const { id } = useParams();
  const { isAdmin, isMaster, isVendor, isPartner, user } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [dlLoading, setDlLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const { data: agreement, isLoading } = useQuery({
    queryKey: ["agreement", id],
    queryFn: () => getAgreement(id),
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["agreement", id] });
    qc.invalidateQueries({ queryKey: ["addendums", id] });
  }

  const uploadFile = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return uploadAgreementFile(id, fd);
    },
    onSuccess: () => {
      setUploadError(null);
      invalidate();
    },
    onError: (err) => setUploadError(err),
  });

  async function downloadFile() {
    setDlLoading(true);
    try {
      const { url } = await getAgreementFileUrl(id);
      window.open(url, "_blank");
    } catch (err) {
      alert(err.message);
    } finally {
      setDlLoading(false);
    }
  }

  if (isLoading) return <Spinner />;
  if (!agreement) return <p className="text-gray-500">Agreement not found.</p>;

  const counterparty = agreement.vendor ?? agreement.partner;
  const isOwner =
    (isVendor && agreement.vendorId === user?.userId) ||
    (isPartner && agreement.partnerId === user?.userId);
  const canAddAddendum = isOwner || isMaster;

  return (
    <div>
      <Link
        to="/agreements"
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ChevronLeft size={15} /> Back to agreements
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">
              {agreement.title}
            </h1>
            <StatusBadge status={agreement.status} />
          </div>
          <p className="text-sm text-gray-500">
            {agreement.entityCache?.name}
            {counterparty &&
              ` · ${agreement.counterpartyType === "VENDOR" ? "Vendor" : "Partner"}: ${counterparty.companyName}`}
          </p>
        </div>
      </div>

      {uploadError && (
        <div className="mb-4">
          <ErrorMessage error={uploadError} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-6">
            <h2 className="font-semibold text-gray-900 mb-4">
              Agreement Details
            </h2>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ["Scope", agreement.scope],
                ["Object", agreement.objectOfAgreement],
                ["Stakeholders", agreement.stakeholders],
                ["Start Date", formatDate(agreement.startDate)],
                ["End Date", formatDate(agreement.endDate)],
                ["Payment Deadline", formatDate(agreement.paymentDeadline)],
                ["Payment Status", agreement.paymentStatus],
                ["Payment Date", formatDate(agreement.paymentDate)],
                ["Submitted", formatDate(agreement.submittedAt)],
                ["Approved", formatDate(agreement.approvedAt)],
                ["Reviewed by", agreement.reviewedBy?.name],
              ]
                .filter(([, v]) => v && v !== "—")
                .map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">
                      {k}
                    </dt>
                    <dd className="text-gray-900 font-medium">{v}</dd>
                  </div>
                ))}
            </dl>
            {agreement.reviewComment && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs font-semibold text-amber-700 mb-1">
                  Review Comment
                </p>
                <p className="text-sm text-amber-800">
                  {agreement.reviewComment}
                </p>
              </div>
            )}
          </div>

          {/* Draft exchange — only for COLLABORATIVE/INTERNAL agreements */}
          {(agreement.agreementType === "COLLABORATIVE" ||
            agreement.agreementType === "INTERNAL") && (
            <DraftExchangeSection
              agreement={agreement}
              isMaster={isMaster}
              isVendor={isVendor}
              isOwner={isOwner}
              onDone={invalidate}
            />
          )}

          <AddendumTimeline
            agreementId={id}
            canSubmit={canAddAddendum}
            agreementStatus={agreement.status}
            isMaster={isMaster}
            isOwner={isOwner}
            user={user}
            onReviewed={invalidate}
            allLogs={agreement.logs}
            agreementEntityCache={agreement.entityCache}
          />

          {/* Activity log */}
          {agreement.logs?.length > 0 && (
            <div className="card">
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Activity Log</h2>
              </div>
              <div className="px-6 py-4">
                <div className="relative">
                  <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-100" />
                  <div className="space-y-3">
                    {agreement.logs.map((log) => (
                      <div key={log.id} className="relative pl-8">
                        <div className="absolute left-1.5 top-1.5 w-3 h-3 rounded-full border-2 border-white bg-gray-300" />
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-medium text-gray-800">
                              <span className="text-gray-500">
                                {log.actorName}
                              </span>
                              {" · "}
                              <span>
                                {ACTION_LABELS[log.action] ?? log.action}
                              </span>
                              {log.toStatus && (
                                <span className="ml-1.5">
                                  <StatusBadge status={log.toStatus} />
                                </span>
                              )}
                            </p>
                            {log.comment && (
                              <p className="text-xs text-gray-500 mt-0.5 italic">
                                "{log.comment}"
                              </p>
                            )}
                            {log.addendumId && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                Addendum action
                              </p>
                            )}
                          </div>
                          <p className="text-xs text-gray-400 flex-shrink-0">
                            {formatDate(log.createdAt)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {counterparty && (
            <div className="card p-5">
              <h3 className="font-semibold text-gray-900 mb-3">
                {agreement.counterpartyType === "VENDOR" ? "Vendor" : "Partner"}
              </h3>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-xs text-gray-500 uppercase tracking-wide">
                    Company
                  </dt>
                  <dd className="font-medium text-gray-900">
                    {counterparty.companyName}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-gray-500 uppercase tracking-wide">
                    Email
                  </dt>
                  <dd className="text-gray-700">{counterparty.email}</dd>
                </div>
              </dl>
            </div>
          )}

          <div className="card p-5">
            <h3 className="font-semibold text-gray-900 mb-2">Rhaya Entity</h3>
            {agreement.entityCache ? (
              <>
                <p className="text-sm font-medium text-gray-900">
                  {agreement.entityCache.name}
                </p>
                {agreement.entityCache.code && (
                  <p className="text-xs text-gray-500 font-mono mt-0.5">
                    {agreement.entityCache.code}
                  </p>
                )}
                {isAdmin && (
                  <Link
                    to={`/entities/${agreement.entityCache.id}`}
                    className="text-xs text-brand-500 hover:underline mt-2 inline-block"
                  >
                    View entity →
                  </Link>
                )}
              </>
            ) : (
              <p className="text-xs text-amber-600 italic mb-2">
                Not yet assigned — Legal team will assign an entity.
              </p>
            )}

            {/* Inline entity assignment — master admin, DRAFT or UNDER_REVIEW only */}
            {isMaster &&
              ["DRAFT", "UNDER_REVIEW"].includes(agreement.status) && (
                <EntityAssignPanel agreement={agreement} onDone={invalidate} />
              )}
          </div>

          {/* PDF card */}
          <div className="card p-5">
            <h3 className="font-semibold text-gray-900 mb-3">
              Agreement Document
            </h3>
            {agreement.fileUrl ? (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 truncate">
                  {agreement.fileName}
                </p>
                <button
                  onClick={downloadFile}
                  disabled={dlLoading}
                  className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
                >
                  <Download size={15} />
                  {dlLoading
                    ? "Getting link..."
                    : "Download current agreement PDF"}
                </button>
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic mb-3">
                No PDF uploaded yet
              </p>
            )}
            {(isOwner || isMaster) &&
              !["TERMINATED", "EXPIRED"].includes(agreement.status) && (
                <>
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadFile.isPending}
                    className={`w-full flex items-center justify-center gap-2 text-sm btn-secondary ${agreement.fileUrl ? "mt-2" : ""}`}
                  >
                    <Upload size={15} />
                    {uploadFile.isPending
                      ? "Uploading..."
                      : agreement.fileUrl
                        ? "Replace PDF"
                        : "Upload PDF"}
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadFile.mutate(file);
                      e.target.value = "";
                    }}
                  />
                </>
              )}
            {uploadError && (
              <div className="mt-2">
                <ErrorMessage error={uploadError} />
              </div>
            )}
          </div>

          {/* Vendor/partner can edit when they own it and it's in an editable state */}
          {isOwner && (
            <DraftEditPanel agreement={agreement} onDone={invalidate} />
          )}

          {/* Master: review the agreement itself */}
          {isMaster && ["DRAFT", "UNDER_REVIEW"].includes(agreement.status) && (
            <ReviewPanel agreement={agreement} onDone={invalidate} />
          )}

          {/* Master: update payment */}
          {isMaster && (
            <PaymentPanel agreement={agreement} onDone={invalidate} />
          )}

          {/* Master: terminate */}
          {isMaster && (
            <TerminatePanel agreement={agreement} onDone={invalidate} />
          )}

          {/* Activity log — all users */}
          {agreement.logs?.length > 0 && (
            <div className="card p-5">
              <h3 className="font-semibold text-gray-900 mb-4">Activity</h3>
              <div className="space-y-3">
                {agreement.logs.map((log) => {
                  // Look up addendum title if this log is for an addendum
                  const addendum = log.addendumId
                    ? agreement.addendums?.find((a) => a.id === log.addendumId)
                    : null;

                  return (
                    <div key={log.id} className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold ${
                            log.actorType === "admin"
                              ? "bg-brand-500"
                              : log.actorType === "vendor"
                                ? "bg-teal-500"
                                : "bg-purple-500"
                          }`}
                        >
                          {log.actorName?.[0]?.toUpperCase() ?? "?"}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-semibold text-gray-900">
                            {log.actorName}
                          </span>
                          <span className="text-xs text-gray-400 capitalize">
                            {log.actorType}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {ACTION_LABELS[log.action] ?? log.action}
                        </p>
                        {addendum && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">
                            Addendum: {addendum.title}
                          </p>
                        )}
                        {log.comment && (
                          <p className="text-xs text-amber-700 mt-1 italic">
                            "{log.comment}"
                          </p>
                        )}
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          {formatDate(log.createdAt)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
